import Vue from 'vue'
import { sortObjectByProperty } from '@/utils/helpers/ObjectSorting'
import { getHeaders, buildUrl, buildUrlWithQuery } from '@/utils/api'
import { payload as batchActionPayload } from '@/components/RouteComponents/Batches/actionPayload'
import { DataLoader } from '@/utils/helpers/DataLoader/DataLoader'

/* Assumes parent component has all necessary data elements
 * e.g. Vuex store, allCodes, reportNotif, vendors, unserializedData, includedTypes, selectedCodes, etc.
 * as well as Transaction mixins
*/
export const reportMixins = {
  data () {
    return {
      // jsonServer: 'https://dvc.localhost',
      jsonServer: 'https://www.revolutiones.com',
      // queryServer: 'https://query.revolutiones.com:8443',
      queryServer: this.$store.getters.getSigmaQueryUrl ? this.$store.getters.getSigmaQueryUrl : 'https://query.revolutiones.com:8443',
      unpostedStatus: ['open']
    }
  },
  methods: {
    // This gets the data needed to run most reports
    getCompanyCodes (additionalActions) {
      return new Promise((resolve, reject) => {
        // Don't get values if already in store as these don't typically change in between report runs
        if (typeof this.$store.getters.getAllCodes.accounts !== 'undefined' && this.$store.getters.getAllCodes.accounts.length > 0) {
          this.allCodes = this.$store.getters.getAllCodes
          resolve(true)
        } else {
          const promises = [
            this.$store.dispatch('fetchAllAccounts'),
            this.$store.dispatch('fetchAllSeries'),
            this.$store.dispatch('fetchAllLocations'),
            this.$store.dispatch('fetchAllSets'),
            this.$store.dispatch('fetchAllFreeFields'),
            this.$store.dispatch('fetchAllTaxCodes'),
            this.$store.dispatch('fetchAllCompanies'),
            this.$store.dispatch('fetchAllProductions'),
            this.$store.dispatch('fetchAllPeriods'),
            this.$store.dispatch('fetchAllSourcecodes')
          ]

          if (additionalActions && Array.isArray(additionalActions)) {
            for (const action of additionalActions) {
              promises.push(this.$store.dispatch(action))
            }
          }
          Promise.all(promises)
            .then(response => {
              this.allCodes = this.$store.getters.getAllCodes
              resolve(true)
            })
            .catch(error => {
              console.log(error)
              reject(error)
            })
        }
      })
    },

    // Function to get all transactions and line items
    // TODO get only transaction data for each report rather than getting all of it
    // TODO get company data, e.g. id__in: companies
    getTransactions (reportOptions) {
      return new Promise((resolve, reject) => {
        /* Below does not work with unserialized data
        //  Search for companies/productions here to limit the data load from the server
        let prodco =
          ((reportOptions.productions) ? '&batch__production__in=' + reportOptions.productions.toString() : '') +
          ((reportOptions.companies) ? '&batch__company__in=' + reportOptions.companies.toString() : '')
        let prodcoLines =
          ((reportOptions.companies) ? '&transaction__batch__company__in=' + reportOptions.companies.toString() : '') +
          ((reportOptions.productions) ? '&transaction__batch__production__in=' + reportOptions.productions.toString() : '')
        */

        // Always get vendors
        const promises = [this.$store.dispatch('fetchAllVendors')]

        // Get only data that is requested
        if (this.includedTypes.includes('aps')) {
          promises.push(this.$store.dispatch('fetchAllAps'))
          promises.push(this.$store.dispatch('fetchAllApLines'))
          promises.push(this.$store.dispatch('fetchAllPos'))
          promises.push(this.$store.dispatch('fetchAllPoLines'))
          promises.push(this.$store.dispatch('fetchAllChecks'))
        }

        if (this.includedTypes.includes('jes')) {
          promises.push(this.$store.dispatch('fetchAllJes'))
          promises.push(this.$store.dispatch('fetchAllJeLines'))
        }

        if (this.includedTypes.includes('pcs') && this.includedTypes.includes('aps')) {
          promises.push(this.$store.dispatch('fetchAllPcs'))
          promises.push(this.$store.dispatch('fetchAllPcLines'))
        } else if (this.includedTypes.includes('pcs')) {
          promises.push(this.$store.dispatch('fetchAllAps'))
          promises.push(this.$store.dispatch('fetchAllApLines'))
          promises.push(this.$store.dispatch('fetchAllPcs'))
          promises.push(this.$store.dispatch('fetchAllPcLines'))
        }

        if (this.includedTypes.includes('pos') && !this.includedTypes.includes('aps')) {
          promises.push(this.$store.dispatch('fetchAllPos'))
          promises.push(this.$store.dispatch('fetchAllPoLines'))
        }

        if (this.includedTypes.includes('cks') && this.includedTypes.includes('aps')) {
          promises.push(this.$store.dispatch('fetchAllChecks'))
        } else if (this.includedTypes.includes('cks')) {
          promises.push(this.$store.dispatch('fetchAllAps'))
          promises.push(this.$store.dispatch('fetchAllApLines'))
          promises.push(this.$store.dispatch('fetchAllChecks'))
        }

        /*
        if (this.selectedCodes.companies || this.selectedCodes.productions) {
          promises.push(this.$store.dispatch('fetchBatches'))
        }
        */
        promises.push(this.$store.dispatch('fetchBatchesWithQuery'))

        Promise.all(promises)
          .then(response => {
            this.vendors = this.$store.getters.getAllVendors
            if (this.unserializedData.aps.headers) this.unserializedData.aps.headers = this.$store.getters.getAllAps
            if (this.unserializedData.jes.headers) this.unserializedData.jes.headers = this.$store.getters.getAllJes
            if (this.unserializedData.pcs.headers) this.unserializedData.pcs.headers = this.$store.getters.getAllPcs
            if (this.unserializedData.cks.headers) this.unserializedData.cks.headers = this.$store.getters.getAllChecks
            if (this.unserializedData.pos.headers) this.unserializedData.pos.headers = this.$store.getters.getAllPos

            if (this.unserializedData.aps.lines) this.unserializedData.aps.lines = this.$store.getters.getAllApLines
            if (this.unserializedData.jes.lines) this.unserializedData.jes.lines = this.$store.getters.getAllJeLines
            if (this.unserializedData.pcs.lines) this.unserializedData.pcs.lines = this.$store.getters.getAllPcLines
            if (this.unserializedData.pos.lines) this.unserializedData.pos.lines = this.$store.getters.getAllPoLines
            resolve(true)
          })
          .catch(error => {
            console.log(error)
            reject(error)
          })
      })
    },

    // function to go through all transaction lines and categorize them into account order or transaction order
    parseTransactions (storeType) {
      // Allow only categorizing the data by account or transaction
      if (!storeType) storeType = 'account'
      if (!(storeType === 'account' || storeType === 'transaction' || storeType === 'vendor')) return false
      if (storeType === 'vendor') storeType = 'transaction'

      return new Promise((resolve, reject) => {
        // Allow for transactions to be place in correct queue by period.  Mainly needed for TBs and other reports that need a beginning balance
        let periodType
        let matchingBatches
        const unsorted = {
          previous: [],
          current: [],
          future: []
        }

        // If a company or production is selected, selected only those with matching batches as that's the only way to tell the company/production.
        if (this.selectedCodes.companies || this.selectedCodes.productions) {
          matchingBatches = this.$store.getters.getBatches.filter(({ company, production }) => {
            if (!this.selectedCodes.productions && this.selectedCodes.companies === company) return true
            else if (!this.selectedCodes.companies && this.selectedCodes.productions === production) return true
            else if (this.selectedCodes.companies === company && this.selectedCodes.productions === production) return true
            else return false
          }).map(key => key.id)
        }

        // Load transactions that are marked as "included"
        // this.includedTypes.forEach((transType) => {
        for (const typeIndex in this.includedTypes) {
          const transType = this.includedTypes[typeIndex]
          if (this.unserializedData[transType].headers && Array.isArray(this.unserializedData[transType].headers)) {
            for (const trans of this.unserializedData[transType].headers) {
              // Match data against report criteria
              // For periods, separate current costs from previous and future
              if (typeof this.selectedCodes.periodStart === 'undefined' && typeof this.selectedCodes.periodStart === 'undefined') {
                periodType = 'current'
              } else if (!trans.period) {
                periodType = 'future'
              } else if (typeof this.selectedCodes.periodStart !== 'undefined' && trans.period < this.selectedCodes.periodStart) {
                periodType = 'previous'
              } else if (typeof this.selectedCodes.periodEnd !== 'undefined' && trans.period > this.selectedCodes.periodEnd) {
                periodType = 'future'
              } else {
                periodType = 'current'
              }

              // hack to skip voids
              if (trans.description.match(/^Voided/) && trans.number.match(/^VX/)) {
                // Skip
                continue
              }

              // If a user source code is selected, it will only show JEs with the matching code.  APs and other transactions will be skipped
              if (transType !== 'jes' && this.selectedCodes.sourcecodes.length > 0) {
                // console.log('Skipping Transaction: ' + trans.id + ',' + trans.source_code)
                // console.log(trans)
                continue
              } else if (transType === 'jes' && this.selectedCodes.sourcecodes.length > 0 && !this.selectedCodes.sourcecodes.includes(trans.source_code)) {
                // console.log('Skipping Transaction: ' + trans.id + ',' + trans.source_code)
                // console.log(trans)
                continue
              }

              // Search only for matching batches
              if (matchingBatches && !matchingBatches.includes(trans.batch)) {
                // console.log('Skipping Batch: ' + trans.id + ',' + trans.batch)
                // console.log(trans)
                continue
              }

              // Look at transactions with the following statuses
              if (this.reportOptions.transactionStatuses.includes(trans.status.toLowerCase())) {
                // Get the lines and match them to transactions
                let lines
                if (this.unserializedData[transType].lines && Array.isArray(this.unserializedData[transType].lines)) {
                  lines = this.unserializedData[transType].lines.filter((rawLine) => {
                    // this is necessary because we are using both serialized and non-serialized data.
                    if (rawLine.hasOwnProperty('transaction_id') && rawLine.transaction_id === trans.id) return rawLine
                    else if (rawLine.hasOwnProperty('transaction') && rawLine.transaction === trans.id) return rawLine
                    // else if (rawLine.hasOwnProperty('number') && rawLine.number === trans.id) return rawLine
                  })

                // Since checks have no distribution lines, we need to create fake lines foreach each.  One for the check, and the other, the offset
                } else if (transType === 'cks') {
                  const lineClearing = Object.assign({}, trans)
                  lineClearing.account = trans.account_clearing
                  lineClearing.amount_decimal = trans.amount_decimal
                  lineClearing.asset = false
                  lineClearing.description = trans.description
                  lineClearing.period = trans.period
                  lineClearing.status = trans.status
                  lineClearing.transaction = trans.id
                  lineClearing.check_number = trans.number

                  lineClearing.date = trans.date
                  lineClearing.check_printed = trans.time_posted
                  lineClearing.vendor = trans.vendor
                  lineClearing.third_party_vendor = trans.vendor
                  lineClearing.related_transactions = trans.related_transactions

                  // Dupe the cash line and update the amounts
                  const lineCash = Object.assign({}, lineClearing)
                  lineCash.account = trans.account_cash
                  lineCash.amount_decimal = parseFloat(trans.amount_decimal) * -1

                  lines = []
                  lines.push(lineCash)
                  lines.push(lineClearing)
                }

                // lines = this.unserializedData[transType].lines.filter(({ transaction }) => transaction === trans.id)
                if (lines && lines.length > 0) {
                  for (const line of lines) {
                    let lineFixed, code, typeLabel

                    const accountType = this.allCodes.accounts.find(({ id }) => { return (id === line.account) }).type
                    /*
                    // Collapse expenses
                    if (typeof this.collapseExpenses !== 'undefined' && this.collapseExpenses === true && accountType.match(/^expense/i)) {
                      line.account = ''
                      line.account_name = 'All Expenses'
                      lineFixed = line
                    }
                    */

                    if (typeof this.expensesOnly !== 'undefined' && this.expensesOnly === true && !accountType.match(/^expense/i)) {
                      lineFixed = undefined
                    } else {
                      // lookup account number by user code, not id
                      code = this.allCodes.accounts.find(({ id }) => { return (id === line.account) }).code

                      // Ignore accounts if user decides
                      if (this.selectedCodes.accounts.length > 0 && !this.selectedCodes.accounts.includes(line.account)) {
                        continue
                      }

                      // Use the label for the transaction type, e.g. "AP" instead of "aps"
                      // ALSO Transform "JE" source code into "PR" if the user_source_code = "PR"
                      if (trans.source_code === this.allCodes.sourcecodes.find(({ code }) => code === 'PR').id) {
                        typeLabel = 'PR'
                      } else {
                        typeLabel = this.transactionTypes[transType].name
                      }

                      // Lookup/transform data to the line
                      // By design, transformLine() will result in a transaction not meeting the criteria and will unset the line
                      lineFixed = this.transformLine(line, trans, typeLabel)
                      if (lineFixed === undefined) continue
                    }

                    // console.log('Transaction')
                    // console.log(trans)
                    // Place into account or transaction sorting
                    // POs don't have trans IDs
                    if (storeType === 'transaction' && transType === 'pos') {
                      if (!unsorted[periodType]['PO' + trans.id]) {
                        const transaction = {}
                        transaction.id = trans.id || ''
                        transaction.batch = trans.batch || ''
                        transaction.date = trans.date || ''
                        transaction.trans_number = 'PO' + trans.id // normally ledger, but for POs, not ledger id
                        transaction.period = trans.period || ''
                        transaction.status = trans.status || ''
                        transaction.time_posted = trans.time_posted || ''
                        transaction.transaction_type = 'PO'
                        transaction.transaction_desc = trans.description || ''
                        transaction.original_amount = trans.amount_decimal || ''
                        transaction.document_amount = 0 // Need to replace this with remaining PO lines
                        transaction.doc_number = trans.number || ''
                        transaction.doc_date = trans.date || ''
                        transaction.effective_date = trans.effective_date || ''
                        transaction.vendor_id = trans.vendor
                        transaction.vendor = lineFixed.vendor_name
                        transaction.lines = []
                        unsorted[periodType]['PO' + trans.id] = transaction
                      }
                      unsorted[periodType]['PO' + trans.id].lines.push(lineFixed)
                      unsorted[periodType]['PO' + trans.id].document_amount += lineFixed.amount

                    // items in the leger, e.g. posted and closed
                    } else if (storeType === 'transaction' && trans.ledger) {
                      if (!unsorted[periodType][trans.ledger]) {
                        const transaction = {}
                        transaction.id = trans.id || ''
                        transaction.batch = trans.batch || ''
                        transaction.date = trans.date || ''
                        transaction.trans_number = trans.ledger || ''
                        transaction.period = trans.period || ''
                        transaction.status = trans.status || ''
                        transaction.time_posted = trans.time_posted || ''
                        transaction.transaction_type = typeLabel
                        transaction.transaction_desc = trans.description || ''
                        transaction.document_amount = trans.amount_decimal || ''
                        transaction.doc_number = trans.number || ''
                        transaction.doc_date = trans.date || ''
                        transaction.effective_date = trans.effective_date || ''
                        transaction.vendor_id = line.vendor
                        transaction.vendor = lineFixed.vendor_name
                        transaction.lines = []
                        unsorted[periodType][trans.ledger] = transaction
                        /*
                        unsorted[periodType][trans.ledger] = Object.assign({}, trans)
                        unsorted[periodType][trans.ledger].lines = []
                        */
                      }
                      unsorted[periodType][trans.ledger].lines.push(lineFixed)

                    // Not sure what to do here.
                    } else if (storeType === 'transaction') {

                    // Vendor
                    } else if (storeType === 'vendor') {
                      if (!unsorted[periodType][trans.vendor]) {
                        unsorted[periodType][trans.vendor] = {}
                        unsorted[periodType][trans.vendor].lines = []
                      }
                      unsorted[periodType][trans.vendor].lines.push(lineFixed)

                    // If not transaction or vendor, then categorizing by account
                    // } else if (storeType === 'account') {
                    } else {
                      // Create an empty array to push items to
                      if (!unsorted[periodType][code]) unsorted[periodType][code] = []
                      unsorted[periodType][code].push(lineFixed)
                    }
                  }
                } else {
                  console.log('No lines for ' + transType + ' transaction ' + trans.id)
                  // reject('No lines for ' + transType + ' transaction ' + trans.id)
                }
              }
            }
          } else {
            reject('No data')
          }
        } // )
        resolve(unsorted)
      })
    },

    // Sums up amounts within an object.  If accountType and type are specific, return DR/CR only.
    sumRecursive (object, amountField, type = null, accountType = null) {
      // Figure out debits and credits
      let sign = null
      if (type && accountType) {
        if (type !== 'debits' && type !== 'credits') {
          type = null
          accountType = null
        } else if (type === 'debits' && (accountType === 'assets' || accountType === 'expenses')) {
          sign = 'positive'
        } else if (type === 'credits' && (accountType === 'assets' || accountType === 'expenses')) {
          sign = 'negative'
        } else if (type === 'debits') {
          sign = 'negative'
        } else if (type === 'credits') {
          sign = 'positive'
        }
      }

      const parseAmount = (value) => {
        return parseFloat(value).toFixed(2)
      }

      const sum = Object.keys(object).reduce(function (prev, current, index) {
        if (!sign) {
          return prev + (+parseAmount(object[current][amountField]))
        } else if (+parseAmount(object[current][amountField]) > 0 && sign === 'positive') {
          return prev + (+parseAmount(object[current][amountField]))
        } else if (+parseAmount(object[current][amountField]) < 0 && sign === 'negative') {
          // return prev - (+object[current][amountField] * -1)
          return prev + (+parseAmount(object[current][amountField]) * 1)
        } else {
          return prev
        }
      }, 0)

      if (isNaN(sum) || sum.toFixed(2) === '-0.00') {
        return '0.00'
      } else {
        return sum.toFixed(2)
      }
    },

    // Works with Stephen's HTG server to post a JSON file somewhere to allow Jasper to see it.
    getJsonUrl (json) {
      return new Promise((resolve, reject) => {
        const data = { json }
        return Vue.http.post(this.jsonServer + '/restV2/addJson', data)
          .then(response => {
            if (response.status === 201 || response.status === 200) {
              resolve(response.data)
            } else {
              reject(response)
            }
          })
          .catch(error => reject(error))
      })
    },

    // Works with Stephen's HTG server to render a PDF file somewhere to allow this to see it.
    getReportUrl (url) {
      return new Promise((resolve, reject) => {
        const data = { url }
        return Vue.http.post(this.jsonServer + '/restV2/getReport', data)
          .then(response => {
            if (response.status === 201 || response.status === 200) {
              return resolve(this.jsonServer + response.data)
            } else {
              return reject(response)
            }
          })
          .catch(error => reject(error))
      })
    },

    async getLedgerDataById (ledgerId) {
      const instanceUrl = this.$store.getters.getActiveInstance.url
      const headers = getHeaders(this.$store.getters.getToken)

      return new Promise((resolve, reject) => {
        return Vue.http.get(buildUrlWithQuery(`ledger/${ledgerId}`, null, instanceUrl), { headers })
          .then(response => {
            if (response.status === 200) {
              return resolve(response.data)
            } else {
              return reject(response)
            }
          })
          .catch(error => reject(error))
      })
    },

    // Works with Stephen's App to get direct SQL
    getReportData (report, options = {}) {
      const instance = this.$store.getters.getActiveInstance
      const url = this.queryServer
      options.instanceUrl = (instance.url.endsWith('/') ? instance.url.slice(0, -1) : instance.url)
      options.jwt = instance.token

      const queryString = '?' + Object.keys(options).map(key => key + '=' + options[key]).join('&')

      const reportTranslation = {
        glreport: '/reports/getalltransactions',
        taxreport: '/reports/get1099transactions',
        vendorreport: '/reports/getvendortransactions',
        trialbalance: '/reports/gettrialbalance',
        payrollinterface: '/reports/getpayrollinterface',
        employeeytd: '/reports/getemployeeytd'
      }

      /*
      let headers = {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
      */
      const postdata = {
        selectedCodes: options.selectedCodes || this.selectedCodes,
        statusTypes: this.reportOptions.includeunposted ? this.reportOptions.transactionStatuses.concat(this.unpostedStatus) : this.reportOptions.transactionStatuses,
        transactionTypes: this.includedTypes,
        reportCurrencyFactor: this.reportOptions.report_currency_id ? (this.allCodes.currencies.find(({ id }) => id === this.reportOptions.report_currency_id) || {}).rate_vs_usd : 1,
        includePOs: typeof this.reportOptions.includePOs === 'undefined' ? false : this.reportOptions.includePOs
      }

      if (this.reportOptions.summarizeExpenses) {
        postdata.expense = this.reportOptions.expenses
      }

      // console.log(url + reportTranslation[report] + queryString)
      return new Promise((resolve, reject) => {
        return Vue.http.post(url + reportTranslation[report] + queryString, postdata)
          .then(response => {
            // console.log(response.data)
            if (response.status === 200) {
              return resolve(response.data)
            } else {
              return reject(response)
            }
          })
          .catch(error => reject(error))
      })
    },

    async getMultiCurrencyReportData (report, options = {}) {
      const instance = this.$store.getters.getActiveInstance
      const url = this.queryServer
      options.instanceUrl = (instance.url.endsWith('/') ? instance.url.slice(0, -1) : instance.url)
      options.jwt = instance.token

      const queryString = '?' + Object.keys(options).map(key => key + '=' + options[key]).join('&')

      const reportTranslation = {
        glreport: '/reports/getalltransactions',
        trialbalance: '/reports/gettrialbalance'
      }

      const promises = []
      const reportCurrencyFactor = this.allCodes.currencies.find(({ id }) => id === this.reportOptions.report_currency_id)
      const getReportCurrencyFactor = (currency) => {
        return parseFloat(reportCurrencyFactor.rate_vs_usd / currency.rate_vs_usd)
      }
      const selectedCurrencies = this.selectedCodes.currency_id && this.selectedCodes.currency_id.length ? this.selectedCodes.currency_id : this.allCodes.currencies.map((c) => c.id)

      for (const cur in selectedCurrencies) {
        const ccurrency = this.allCodes.currencies.find(({ id }) => id === selectedCurrencies[cur])

        const postdata = {
          selectedCodes: { ...this.selectedCodes, ...{ currency_id: selectedCurrencies[cur] } },
          statusTypes: this.reportOptions.includeunposted ? this.reportOptions.transactionStatuses.concat(this.unpostedStatus) : this.reportOptions.transactionStatuses,
          transactionTypes: this.includedTypes,
          reportCurrencyFactor: getReportCurrencyFactor(ccurrency),
          includePOs: typeof this.reportOptions.includePOs === 'undefined' ? false : this.reportOptions.includePOs
        }

        if (this.reportOptions.summarizeExpenses) {
          postdata.expense = this.reportOptions.expenses
        }

        const promise = new Promise((resolve, reject) => {
          return Vue.http.post(url + reportTranslation[report] + queryString, postdata)
            .then(response => {
              if (response.status === 200) {
                const results = response.data.data
                for (const i in results) {
                  if (i === 'purchaseOrders' && this.reportOptions.includePOs) {
                    for (const p in results[i]) {
                      Object.keys(results[i][p]).forEach((group) => {
                        results[i][p][group].map((obj) => { obj.amount = obj.amount_native * postdata.reportCurrencyFactor })
                      })
                    }
                  } else {
                    Object.keys(results[i]).forEach((group) => {
                      results[i][group].map((obj) => { obj.amount = obj.amount_native * postdata.reportCurrencyFactor })
                    })
                  }
                }
                return resolve(results)
              } else {
                return reject(response)
              }
            })
            .catch(error => reject(error))
        })
        promises.push(promise)
      }
      return Promise.all(promises)
        .then(resp => {
          const results = {}
          for (const i in resp) {
            Object.keys(resp[i]).forEach((group) => {
              if (results.hasOwnProperty(group)) {
                if (group === 'purchaseOrders' && this.reportOptions.includePOs) {
                  Object.keys(resp[i][group]).forEach((pgroup) => {
                    if (results[group].hasOwnProperty(pgroup)) {
                      Object.keys(resp[i][group][pgroup]).forEach((acct) => {
                        if (Array.isArray(resp[i][group][pgroup][acct])) {
                          if (results[group][pgroup][acct]) {
                            for (const newAcct in resp[i][group][pgroup][acct]) {
                              results[group][pgroup][acct].push(resp[i][group][pgroup][acct][newAcct])
                            }
                          } else {
                            results[group][pgroup][acct] = resp[i][group][pgroup][acct]
                          }
                        }
                      })
                    } else {
                      results[group][pgroup] = resp[i][group][pgroup]
                    }
                  })
                } else {
                  Object.keys(resp[i][group]).forEach((acct) => {
                    if (Array.isArray(resp[i][group][acct])) {
                      if (results[group][acct]) {
                        for (const newAcct in resp[i][group][acct]) {
                          results[group][acct].push(resp[i][group][acct][newAcct])
                        }
                      } else {
                        results[group][acct] = resp[i][group][acct]
                      }
                    }
                  })
                }
              } else {
                results[group] = resp[i][group]
              }
            })
          }
          return results
        })
    },

    // Works with Stephen's App to get direct SQL
    getCompanyData (report, options = {}) {
      const instance = this.$store.getters.getActiveInstance
      const url = this.queryServer
      options.instanceUrl = (instance.url.endsWith('/') ? instance.url.slice(0, -1) : instance.url)
      options.jwt = instance.token

      const queryString = '?' + Object.keys(options).map(key => key + '=' + options[key]).join('&')

      const allCodesUrl = '/codes/getallcodes'

      return new Promise((resolve, reject) => {
        return Vue.http.get(url + allCodesUrl + queryString)
          .then(response => {
            // console.log(response.data)
            if (response.status === 200) {
              return resolve(response.data)
            } else {
              return reject(response)
            }
          })
          .catch(error => reject(error))
      })
    },

    // Works with Stephen's App to get direct SQL
    getVendorData (report, options = {}) {
      const instance = this.$store.getters.getActiveInstance
      const url = this.queryServer
      options.instanceUrl = (instance.url.endsWith('/') ? instance.url.slice(0, -1) : instance.url)
      options.jwt = instance.token

      const queryString = '?' + Object.keys(options).map(key => key + '=' + options[key]).join('&')

      const allCodesUrl = '/codes/getallvendors'

      return new Promise((resolve, reject) => {
        return Vue.http.get(url + allCodesUrl + queryString)
          .then(response => {
            // console.log(response.data)
            if (response.status === 200) {
              return resolve(response.data)
            } else {
              return reject(response)
            }
          })
          .catch(error => reject(error))
      })
    },

    // Replacement for getVendorData using API
    async getAllVendors () {
      return await this.$store.dispatch('fetchAllVendorModels')
        .then(response => {
          return (response && Array.isArray(response)) ? response : []
        })
        .then(vendors => {
          const states = vendors.map(ven => (ven.address_selected || {}).state)
          this.$store.dispatch('fetchStatesWithQuery', { id__in: states })
          return vendors
        })
        .then(vendors => {
          return vendors
        })
        .catch(error => {
          console.log(error)
        })
    },

    // AP Aging Report
    async getAgingReportData (reportUrl, options = {}) {
      const instanceUrl = this.$store.getters.getActiveInstance.url
      const headers = getHeaders(this.$store.getters.getToken)
      const postdata = this.reportOptions

      return new Promise((resolve, reject) => {
        return Vue.http.post(buildUrl(reportUrl, null, instanceUrl), postdata, { headers })
          .then(response => {
            if (response.status === 200) {
              return resolve(response.data)
            } else {
              return reject(response)
            }
          })
          .catch(error => reject(error))
      })
    },

    /**
     * Retrieves list of active batches
     */
    async getBatchList () {
      return new Promise((resolve, reject) => {
        let dataLoader = new DataLoader(this.$store, [batchActionPayload], true, 200)
        Promise.all([dataLoader.loadData()])
          .then(() => {
            this.batches = this.$store.getters.getAllBatches
            resolve(true)
          })
          .catch(error => {
            console.log(error)
            reject(error)
          })
      })
    },

    // Report popup box
    createNotification (title, message, type, url, fileType, filename, duration = 0) {
      if (!message) message = 'Report Ready. '

      if (url) {
        const newEl = this.$createElement
        message = newEl('p', null, [
          newEl('span', null, message + ' '),
          newEl('button', { on: { click: () => { this.openReport(url, '_reports') } } }, 'Open')
        ])
      }

      // Non-pdf output type (only txt and csv allowed for now)
      if (fileType) {
        const newEl = this.$createElement
        const extension = (fileType === 'txt') ? 'txt' : 'csv'
        message = newEl('p', null, [
          newEl('span', null, message + ' '),
          newEl('button', { on: { click: () => { this.downloadCsv(filename, extension) } } }, 'Download')
        ])
      }

      this.reportNotif = this.$message({
        title: title || 'Message',
        type: type || 'info',
        duration,
        message,
        showClose: true,
        onClose: this.disableReportRun = false
      })
    },

    // Helper function to launch the report in a different tab
    openReport (url) {
      this.reportNotif.close()
      this.disableReportRun = false

      if (!url) return false
      window.open(url, '_reports')
    },

    downloadCSVfromAPI (payload, filename) {
      const instanceUrl = this.$store.getters.getActiveInstance.url
      const headers = getHeaders(this.$store.getters.getToken)
      let { query, type, reportId } = payload
      return Vue.http.get(buildUrlWithQuery(`reports/${type}`, query, instanceUrl), { headers })
        .then((res) => res.text())
        .then(r => {
          if (!r) return this.$store.commit('RECEIVE_REPORT_FAILURE', 'Error building CSV Report')
          this.prettyJson = r
          this.createNotification('Success', 'CSV is Ready. ', 'success', false, true, filename)
        })
        .catch(e => {
          console.log(e)
          this.$store.commit('RECEIVING_REPORT', { status: false, reportId })
          this.$store.commit('RECEIVE_REPORT_FAILURE', { err: e.code, msg: e.statusText, reportId })
        })
    },

    downloadCsv (name, extension) {
      if (this.reportNotif) this.reportNotif.close()
      this.disableReportRun = false

      if (!this.prettyJson) return false
      const filename = (name || 'GL-BibleReport') + '.' + extension
      const blob = new Blob([this.prettyJson], { type: 'text/' + (extension === 'txt' ? 'plain' : 'csv') + ';charset=utf-8;' })
      if (navigator.msSaveBlob) { // IE 10+
        navigator.msSaveBlob(blob, filename)
      } else {
        const csvElement = document.createElement('a')
        const url = URL.createObjectURL(blob)
        csvElement.setAttribute('href', url)
        csvElement.setAttribute('download', filename)
        csvElement.setAttribute('target', '_blank')
        csvElement.style.visibility = 'hidden'
        document.body.appendChild(csvElement)
        csvElement.click()
        document.body.removeChild(csvElement)
      }
    },

    // Use the JSON in our hidden box to create a report
    generateCustomReport () {
      this.loading = true
      this.disableReportRun = true
      this.getJsonUrl(JSON.parse(this.prettyJson))
        .then(resJsonUrl => {
          // PDF version of the report
          const report = this.jasperSettings.reportServer + this.jasperSettings.reportName + '.pdf?net.sf.jasperreports.json.source=' + resJsonUrl
          // HTML version of the report
          const preview = this.jasperSettings.reportServer + this.jasperSettings.reportName + '.html?net.sf.jasperreports.json.source=' + resJsonUrl
          console.log(preview)
          return this.getReportUrl(report)
        })
        .then(reportUrl => {
          this.loading = false
          this.createNotification('Success', 'Report is Ready. ', 'success', reportUrl)
        })
        .catch(error => {
          this.loading = false
          console.log(error)
        })
    },

    async getRawReport (report = null, paramId = null) {
      const instanceUrl = this.$store.getters.getActiveInstance.url
      const headers = getHeaders(this.$store.getters.getToken)
      const query = { cached_parameters: paramId }
      return Vue.http.get(buildUrlWithQuery(`reports/${report}/raw`, query, instanceUrl), { headers })
        .then(response => {
          // console.log(response)
          if (!response || response.status !== 200 || !response.data) return false
          else return response.data
        })
        .catch(e => {
          // console.log(e)
          return false
        })
    },

    // Store rendered report in the database for future retreival
    storeRenderedReport (reportName = null, type = 'PDF', reportUrl = null) {
      if (!reportName || !reportUrl) {
        return false
      }

      const instanceUrl = this.$store.getters.getActiveInstance.url
      const username = this.$store.getters.getUser.username
      const headers = getHeaders(this.$store.getters.getToken)
      const postdata = {
        generated_by: username,
        status: 'DONE',
        name: reportName,
        encoding: 'BASE64',
        type
      }

      Vue.http.get(reportUrl, { responseType: 'arraybuffer' })
        .then(report => {
          const pdfData = this.arrayBufferToBase64(report.data)
          return pdfData
        })
        .then(reportBody => {
          postdata.body = reportBody
          return Vue.http.post(buildUrl(`reports/renders`, null, instanceUrl), postdata, { headers })
        })
        .then(response => {
          if (response.status === 201) {
            return true
          } else {
            return false
          }
        })
        .catch(e => {
          console.log(e)
          return false
        })
    },

    // Convert raw strings into a base64 string that can be safely stored in a database
    arrayBufferToBase64 (buffer) {
      if (!buffer) return false

      const codeArray = new Uint8Array(buffer)
      const binary = String.fromCharCode.apply(null, codeArray)

      return btoa(binary)
    },

    // Function to get today's date the way Jasper wants it
    today () {
      const date = new Date()
      const mm = date.getMonth() + 1
      const dd = date.getDate()

      return [date.getFullYear(),
        (mm > 9 ? '' : '0') + mm,
        (dd > 9 ? '' : '0') + dd
      ].join('/')
    },

    // Sort an array of transactions
    sortTransactions (unsorted) {
      return new Promise((resolve, reject) => {
        // Sort by accounts
        // TODO make sure list of accounts are sorted
        const sortedData = unsorted
        // Sort accounts by transaction numbers
        const finalSorted = []

        for (const i in sortedData) {
          if (sortedData.hasOwnProperty(i)) {
            if (!Array.isArray(sortedData[i])) resolve(unsorted)
            finalSorted[i] = sortObjectByProperty(sortedData[i], 'trans_number')
          }
        }
        resolve(finalSorted)
      })
    },

    // Sort an array of an array of transactions
    sortArrayOfTransactions (unsorted, propertyToSort) {
      if (!propertyToSort) propertyToSort = 'trans_number'
      const finalSorted = []

      return new Promise((resolve, reject) => {
        for (const index in unsorted) {
          // Sort by accounts
          // TODO make sure list of accounts are sorted
          const sortedData = unsorted[index]
          finalSorted[index] = []

          // Sort accounts by transaction numbers
          for (const i in sortedData) {
            if (sortedData.hasOwnProperty(i)) {
              if (!Array.isArray(sortedData[i])) resolve(unsorted)
              finalSorted[index][i] = sortObjectByProperty(sortedData[i], propertyToSort)
            }
          }
        }
        resolve(finalSorted)
      })
    },

    // Get the line data from the transction and convert the ids into accounting segments
    transformLine (line, transaction, type) {
      if (!line || !transaction || !type) return undefined

      // Skip unselected data
      if (this.selectedCodes.series.length > 0 && !this.selectedCodes.series.includes(line.series)) {
        // console.log('Skipping line: ' + line.id + ',' + line.series)
        // console.log(line)
        return undefined
      } else if (this.selectedCodes.locations.length > 0 && !this.selectedCodes.locations.includes(line.location)) {
        // console.log('Skipping line: ' + line.id + ',' + line.location)
        // console.log(line)
        return undefined
      } else if (this.selectedCodes.sets.length > 0 && !this.selectedCodes.sets.includes(line.set)) {
        // console.log('Skipping line: ' + line.id + ',' + line.set)
        // console.log(line)
        return undefined
      } else if (this.selectedCodes.freefield1.length > 0 && !this.selectedCodes.freefield1.includes(line.free_field_1)) {
        // console.log('Skipping line: ' + line.id + ',' + line.free_field_1)
        // console.log(line)
        return undefined
      }

      const lineNew = {} // converted line

      // specific data for Cks.  Never here? As there are are no lines?
      if (type === 'CK') {
        // console.log('CK', transaction.number, line.status, line.amount_decimal, line.related_transactions)
        lineNew.amount = line.amount_decimal
        lineNew.period = line.period
        lineNew.status = line.status
        lineNew.check_number = line.number
        lineNew.check_date = line.date
        lineNew.check_printed = line.time_posted
        lineNew.check_vendor = line.vendor
        lineNew.related_transactions = line.related_transactions

        /*
        // breakout the offset line
        let lineNewOffset = Object.assign({}, lineNew)
        lineNewOffset.account = this.allCodes.accounts.find(( {id} ) => { return (id === line.account_clearing) }).code
        */

        // specific data for POs
      } else if (type === 'PO') {
        // console.log('PO', transaction.number, line.type, line.amount_decimal, line.remaining_decimal)
        // console.log(line)
        lineNew.account = this.allCodes.accounts.find(({ id }) => { return (id === line.account) }).code
        lineNew.period = transaction.period
        lineNew.vendor_name = (transaction.vendor) ? this.vendors.find(({ id }) => { return (id === transaction.vendor) }).name : null
        lineNew.vendor_id = transaction.vendor
        if (line.type === 'OPEN' || line.type === 'PARTIAL') {
          // lineNew.amount = (line.amount_decimal*1 - line.remaining_decimal*1)
          lineNew.amount = (line.remaining_decimal * 1) || 0
        } else if (line.type === 'ADJUSTMENT') {
          return undefined
        } else {
          // Ignore
          return undefined
        }
      } else if (type === 'AP') {
        lineNew.amount = line.amount_decimal
        lineNew.account = this.allCodes.accounts.find(({ id }) => { return (id === line.account) }).code
        lineNew.period = transaction.period
        lineNew.vendor_name = (transaction.vendor) ? this.vendors.find(({ id }) => { return (id === transaction.vendor) }).name : null
        lineNew.vendor_id = transaction.vendor
        if (line.relieves) {
          const po = this.unserializedData.pos.headers.find(({ id }) => { return (id === line.relieves) })
          if (po && po.number) lineNew.po_number = po.number
        }

        if (transaction.payment_type === 'CHECK') {
          let payment
          if (this.unserializedData.cks.headers && Array.isArray(this.unserializedData.cks.headers)) {
            payment = this.unserializedData.cks.headers.find(({ related_transactions }) => related_transactions.includes(transaction.id))
          }
          if (payment && payment.number) lineNew.payment_number = payment.number
        } else if (transaction.payment_type === 'CHECK_MANUAL' && transaction.payment_confirmation_number) {
          lineNew.payment_number = transaction.payment_confirmation_number + '(MC)'
        }
      } else {
        lineNew.amount = line.amount_decimal
        lineNew.account = this.allCodes.accounts.find(({ id }) => { return (id === line.account) }).code
        lineNew.period = transaction.period
        lineNew.vendor_name = (transaction.third_party_vendor) ? this.vendors.find(({ id }) => { return (id === transaction.third_party_vendor) }).name : null
        lineNew.vendor_id = transaction.vendor
      }

      // Common to all lines
      lineNew.account_desc = (line.account) ? this.allCodes.accounts.find(({ id }) => { return (id === line.account) }).name : null
      lineNew.account_header = (line.code) ? this.allCodes.accounts.find(({ id }) => { return (id === line.account) }).code : null
      lineNew.series = (line.series) ? this.allCodes.series.find(({ id }) => { return (id === line.series) }).code : null
      lineNew.location = (line.location) ? this.allCodes.locations.find(({ id }) => { return (id === line.location) }).code : null
      lineNew.set = (line.set) ? this.allCodes.sets.find(({ id }) => { return (id === line.set) }).code : null
      lineNew.FF1 = (line.free_field_1) ? this.allCodes.freefield1.find(({ id }) => { return (id === line.free_field_1) }).code : null
      lineNew.FF2 = (line.free_field_2) ? this.allCodes.freefield1.find(({ id }) => { return (id === line.free_field_2) }).code : null
      lineNew.FF3 = (line.free_field_3) ? this.allCodes.freefield1.find(({ id }) => { return (id === line.free_field_3) }).code : null
      lineNew.FF4 = (line.free_field_4) ? this.allCodes.freefield1.find(({ id }) => { return (id === line.free_field_4) }).code : null
      // lineNew.insurance = (line.insurance) ? this.allCodes.insurances.find(({ id }) => { return (id === line.insurance) }).code : null
      // lineNew.asset = (line.asset) ? this.allCodes.assets.find(( {id} ) => { return (id === line.asset) }).code : null
      lineNew.asset = (line.asset) ? line.asset : null
      lineNew.tax_code = (line.tax_code) ? this.allCodes.taxcodes.find(({ id }) => { return (id === line.tax_code) }).code : null
      // lineNew.third_party_vendor = (line.third_party_vendor) ? this.vendors.find(( {id} ) => { return (id === line.third_party_vendor) }).name : null
      lineNew.line_desc = line.description
      lineNew.recon = line.recon

      // Add header info to line
      lineNew.trans_type = type
      if (type === 'PO') lineNew.trans_number = 'PO' + transaction.id // No ledger number for POs
      else lineNew.trans_number = transaction.ledger
      lineNew.doc_date = transaction.date
      lineNew.doc_number = transaction.number

      // Add transaction data to the line for CSVs and stuff
      const batch = this.$store.getters.getBatchById(transaction.batch)
      const company = batch ? this.allCodes.companies.find(({ id }) => id === (batch).company) : undefined
      const production = batch ? this.allCodes.productions.find(({ id }) => id === (batch).production) : undefined

      lineNew.company = company ? company.name : ''
      lineNew.production = production ? production.name : ''
      lineNew.batch = batch ? batch.name : ''

      const transKeys = Object.keys(transaction)
      for (const key in transKeys) {
        lineNew['transaction_' + transKeys[key]] = transaction[transKeys[key]]
      }

      return lineNew
    },

    createGroupByAccountCsv (data, map) {
      if (!data || !Array.isArray(data)) return false
      let csv = ''
      let headerFields
      let dataFields
      let accountGroupTotal = {}
      data.forEach(obj => {
        if (!accountGroupTotal.hasOwnProperty(obj.account)) {
          accountGroupTotal[obj.account] = {
            'account_name': obj.account_name,
            'account_code': obj.account,
            'account_total': obj.amount
          }
        } else {
          accountGroupTotal[obj.account].account_total = accountGroupTotal[obj.account].account_total + obj.amount
        }
      })

      if (map) {
        // Create header Row
        headerFields = Object.keys(map)

        // Get native data labels
        dataFields = Object.values(map)
      } else {
        // Generate a header row based on the first row and dump all data in the array
        headerFields = dataFields = Object.keys(data[0])
      }

      if (headerFields.length === 0) return false

      // csv = '"source",' //  for sipra.  TODO: 1099 output

      for (const i in headerFields) {
        let val = headerFields[i]

        if (typeof val === 'string') {
          val = '"' + val.replace(/"/g, '""') + '"'
        }

        csv += val
        if (parseInt(i) === (headerFields.length - 1)) csv += '\n'
        else csv += ','
      }

      let header = csv
      // now build data rows
      const amountFields = ['amount', 'debits', 'credits']
      let rows = []
      let accountCode = ''
      for (const i in data) {
        let columns = []
        // csv += '"RES",' // TODO: 1099 output
        for (const j in dataFields) {
          let val = data[i][dataFields[j]]
          if (amountFields.includes(dataFields[j].toLowerCase())) {
            val = isNaN ? val : val.toFixed(2)
          } else if (dataFields[j].toLowerCase() === 'payroll_source') {
            val = 'RES'
          } else if (!val) {
            val = ''
          } else if (typeof val === 'string') {
            val = '"' + val.replace(/"/g, '""').replace(/^0(.*$)/, '0$1\t') + '"'
          }

          columns.push(val)
          if (parseInt(j) === (dataFields.length - 1)) {
            let rowAccountCode = columns[6].replaceAll('"', '').replaceAll('\t', '')
            if (accountCode === '') accountCode = rowAccountCode
            if (accountCode !== rowAccountCode) {
              let prevRowAccountCode = accountCode
              accountCode = rowAccountCode
              let s = '"Account Total ' + accountGroupTotal[prevRowAccountCode].account_code + ' ' + accountGroupTotal[prevRowAccountCode].account_name + '"'
              rows.push(['', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', s, '', '', '', accountGroupTotal[prevRowAccountCode].account_total])
              rows.push(['', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''])
              rows.push(['', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''])
            }
            rows.push(columns)
          }
        }
      }
      let finalCsv = header
      rows.forEach(row => {
        finalCsv += row.join(',') + '\n'
      })
      console.log(csv)
      return finalCsv
    },

    // Create a CSV file from an array of data
    // Array keys will be the column headers
    createCsv (data, map) {
      if (!data || !Array.isArray(data)) return false
      let csv = ''
      let headerFields
      let dataFields

      if (map) {
        // Create header Row
        headerFields = Object.keys(map)

        // Get native data labels
        dataFields = Object.values(map)
      } else {
        // Generate a header row based on the first row and dump all data in the array
        headerFields = dataFields = Object.keys(data[0])
      }

      if (headerFields.length === 0) return false

      // csv = '"source",' //  for sipra.  TODO: 1099 output

      for (const i in headerFields) {
        let val = headerFields[i]

        if (typeof val === 'string') {
          val = '"' + val.replace(/"/g, '""') + '"'
        }

        csv += val
        if (parseInt(i) === (headerFields.length - 1)) csv += '\n'
        else csv += ','
      }

      // now build data rows
      const amountFields = ['amount', 'debits', 'credits']

      for (const i in data) {
        // csv += '"RES",' // TODO: 1099 output
        for (const j in dataFields) {
          let val = data[i][dataFields[j]]

          if (amountFields.includes(dataFields[j].toLowerCase())) {
            val = isNaN ? val : val.toFixed(2)
          } else if (dataFields[j].toLowerCase() === 'payroll_source') {
            val = 'RES'
          } else if (!val) {
            val = ''
          } else if (typeof val === 'string') {
            val = '"' + val.replace(/"/g, '""').replace(/^0(.*$)/, '0$1\t') + '"'
          }

          csv += val
          if (parseInt(j) === (dataFields.length - 1)) csv += '\n'
          else csv += ','
        }
      }

      return csv
    },

    getReportOptions (id) {
      const instanceUrl = this.$store.getters.getActiveInstance.url
      const headers = getHeaders(this.$store.getters.getToken)
      const inputParams = { page_size: 500, report_group: this.report_group }

      const url = id ? `reportoptions/${id}` : `reportoptions`
      return new Promise((resolve, reject) => {
        return Vue.http.get(buildUrlWithQuery(url, inputParams, instanceUrl), { headers })
          .then(response => {
            if (response.status === 200) {
              return resolve(response.data)
            } else {
              return reject(response)
            }
          })
          .catch(error => reject(error))
      })
    },
    saveReportOptions (json) {
      return new Promise((resolve, reject) => {
        const headers = getHeaders(this.$store.getters.getToken)
        const instanceUrl = this.$store.getters.getActiveInstance.url
        return Vue.http.post(buildUrl('reportoptions', null, instanceUrl), json, { headers })
          .then(response => {
            if (response.status === 201 || response.status === 200) {
              resolve(response.data)
            } else {
              reject(response)
            }
          })
          .catch(error => reject(error))
      })
    },
    updateReportOptions (json) {
      return new Promise((resolve, reject) => {
        const { data, id } = json
        const headers = getHeaders(this.$store.getters.getToken)
        const instanceUrl = this.$store.getters.getActiveInstance.url
        return Vue.http.patch(buildUrl(`reportoptions/${id}`, null, instanceUrl), data, { headers })
          .then(response => {
            if (response.status === 201 || response.status === 200) {
              resolve(response.data)
            } else {
              reject(response)
            }
          })
          .catch(error => reject(error))
      })
    },
    deleteReportOptions (id) {
      return new Promise((resolve, reject) => {
        const headers = getHeaders(this.$store.getters.getToken)
        const instanceUrl = this.$store.getters.getActiveInstance.url
        return Vue.http.delete(buildUrl(`reportoptions/${id}`, null, instanceUrl), { headers })
          .then(response => {
            if (response.status === 201 || response.status === 200 || response.status === 204) {
              resolve(response.data)
            } else {
              reject(response)
            }
          })
          .catch(error => reject(error))
      })
    }
  }
}



// WEBPACK FOOTER //
// ./src/Mixins/Reports.js